import React, { useEffect, useMemo, useState } from "react";
import type { Entity } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";

type FormValues = Record<string, string | number | boolean | null>;
type EntityForm = Entity & { roleId?: number | null };

// ─── Integration buttons (CNPJ / CEP lookup) ─────────────────────────────────
const EntityIntegrationActions: React.FC<{
  values: FormValues;
  setValues: React.Dispatch<React.SetStateAction<FormValues>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  readOnly: boolean;
}> = ({ values, setValues, setError, readOnly }) => {
  const { client } = useAuth();
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [integrationError, setIntegrationError] = useState<string | null>(null);

  const handleCnpjLookup = async () => {
    const document = String(values.document ?? "").trim();
    if (!document) { setIntegrationError("Informe o CNPJ."); return; }
    setIntegrationError(null); setError(null); setLoadingCnpj(true);
    const response = await client.get(`/integrations/cnpj/${encodeURIComponent(document)}`);
    if (!response.ok) { setIntegrationError("Falha ao consultar CNPJ."); setLoadingCnpj(false); return; }
    const payload = (await response.json()) as {
      data?: {
        razao_social?: string; nome_fantasia?: string; logradouro?: string;
        numero?: string; complemento?: string; bairro?: string;
        municipio?: string; uf?: string; cep?: string;
      };
    };
    setValues((prev) => ({
      ...prev,
      name: payload.data?.razao_social ?? prev.name,
      tradeName: payload.data?.nome_fantasia ?? prev.tradeName,
      street: payload.data?.logradouro ?? prev.street,
      streetNumber: payload.data?.numero ?? prev.streetNumber,
      complement: payload.data?.complemento ?? prev.complement,
      neighborhood: payload.data?.bairro ?? prev.neighborhood,
      city: payload.data?.municipio ?? prev.city,
      state: payload.data?.uf ?? prev.state,
      zipCode: payload.data?.cep ?? prev.zipCode,
    }));
    setLoadingCnpj(false);
  };

  const handleCepLookup = async () => {
    const zip = String(values.zipCode ?? "").trim();
    if (!zip) { setIntegrationError("Informe o CEP."); return; }
    setIntegrationError(null); setError(null); setLoadingCep(true);
    const response = await client.get(`/integrations/cep/${encodeURIComponent(zip)}`);
    if (!response.ok) { setIntegrationError("Falha ao consultar CEP."); setLoadingCep(false); return; }
    const payload = (await response.json()) as {
      data?: { logradouro?: string; bairro?: string; localidade?: string; uf?: string; complemento?: string; cep?: string; };
    };
    setValues((prev) => ({
      ...prev,
      street: payload.data?.logradouro ?? prev.street,
      neighborhood: payload.data?.bairro ?? prev.neighborhood,
      city: payload.data?.localidade ?? prev.city,
      state: payload.data?.uf ?? prev.state,
      complement: payload.data?.complemento ?? prev.complement,
      zipCode: payload.data?.cep ?? prev.zipCode,
    }));
    setLoadingCep(false);
  };

  const isPJ = values.type === "PJ";

  return (
    <div className="mb-3">
      {integrationError && <div className="alert alert-danger py-2">{integrationError}</div>}
      <div className="d-flex flex-wrap gap-2">
        <button type="button" className="btn btn-outline-primary btn-sm"
          onClick={handleCnpjLookup} disabled={readOnly || !isPJ || loadingCnpj}>
          {loadingCnpj ? "Consultando..." : "🔍 Buscar CNPJ"}
        </button>
        <button type="button" className="btn btn-outline-secondary btn-sm"
          onClick={handleCepLookup} disabled={readOnly || loadingCep}>
          {loadingCep ? "Consultando..." : "📍 Buscar CEP"}
        </button>
      </div>
    </div>
  );
};

// ─── Profile badges ───────────────────────────────────────────────────────────
const ProfileBadges: React.FC<{ item: Entity }> = ({ item }) => {
  const badges: { label: string; cls: string }[] = [];
  if (item.isClient)   badges.push({ label: "Cliente",     cls: "text-bg-primary" });
  if (item.isSupplier) badges.push({ label: "Fornecedor",  cls: "text-bg-secondary" });
  if (item.isPartner)  badges.push({ label: "Socio",       cls: "text-bg-warning" });
  if (item.isEmployee) badges.push({ label: "Funcionario", cls: "text-bg-info" });
  if (badges.length === 0) return <span className="text-muted small">—</span>;
  return (
    <div className="d-flex flex-wrap gap-1">
      {badges.map((b) => (
        <span key={b.label} className={`badge ${b.cls}`} style={{ fontSize: "0.7rem" }}>{b.label}</span>
      ))}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
export const EntitiesPage: React.FC = () => {
  const { client } = useAuth();
  const [funcoes, setFuncoes] = useState<Array<{ id: number; name: string }>>([]);

  // Filter state
  const [filterType, setFilterType]       = useState("");
  const [filterProfile, setFilterProfile] = useState("");
  const [filterActive, setFilterActive]   = useState("");

  useEffect(() => {
    let active = true;
    client.get("/modules/funcoes?take=100&skip=0")
      .then((r) => r.json())
      .then((payload) => {
        if (active) setFuncoes((payload as { data: Array<{ id: number; name: string }> }).data);
      })
      .catch(() => { if (active) setFuncoes([]); });
    return () => { active = false; };
  }, [client]);

  const listFilters = useMemo(() => {
    const f: Record<string, unknown> = {};
    if (filterType)    f.type = filterType;
    if (filterProfile) f[filterProfile] = true;
    if (filterActive !== "") f.isActive = filterActive === "true";
    return f;
  }, [filterType, filterProfile, filterActive]);

  const activeCount = [filterType, filterProfile, filterActive].filter(Boolean).length;

  const filterBar = (
    <div
      className="d-flex flex-wrap align-items-center gap-2 px-3 py-2 mb-2 rounded"
      style={{ background: "#f8f9fa", borderTop: "3px solid #0d6efd" }}
    >
      <small className="text-muted fw-semibold me-1">Filtros</small>

      <select
        className="form-select form-select-sm"
        style={{ width: 130 }}
        value={filterType}
        onChange={(e) => setFilterType(e.target.value)}
      >
        <option value="">Tipo: todos</option>
        <option value="PF">Pessoa Física</option>
        <option value="PJ">Pessoa Jurídica</option>
      </select>

      <select
        className="form-select form-select-sm"
        style={{ width: 160 }}
        value={filterProfile}
        onChange={(e) => setFilterProfile(e.target.value)}
      >
        <option value="">Perfil: todos</option>
        <option value="isClient">Cliente</option>
        <option value="isSupplier">Fornecedor</option>
        <option value="isPartner">Sócio</option>
        <option value="isEmployee">Funcionário</option>
      </select>

      <select
        className="form-select form-select-sm"
        style={{ width: 130 }}
        value={filterActive}
        onChange={(e) => setFilterActive(e.target.value)}
      >
        <option value="">Status: todos</option>
        <option value="true">Ativo</option>
        <option value="false">Inativo</option>
      </select>

      {activeCount > 0 && (
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => { setFilterType(""); setFilterProfile(""); setFilterActive(""); }}
        >
          Limpar ({activeCount})
        </button>
      )}
    </div>
  );

  return (
    <CrudPage<EntityForm>
      title="Entidades PF/PJ"
      endpoint="/modules/entities"
      searchPlaceholder="Buscar por nome, documento, email..."
      listFilters={listFilters}
      renderFilters={filterBar}
      columns={[
        {
          label: "Nome",
          render: (item) => (
            <div>
              <div className="fw-semibold">{item.name}</div>
              {item.tradeName && item.tradeName !== item.name && (
                <div className="text-muted small">{item.tradeName}</div>
              )}
            </div>
          ),
        },
        {
          label: "Tipo",
          render: (item) =>
            item.type === "PJ" ? (
              <span className="badge text-bg-primary" style={{ fontSize: "0.7rem" }}>PJ</span>
            ) : (
              <span className="badge text-bg-secondary" style={{ fontSize: "0.7rem" }}>PF</span>
            ),
        },
        { label: "Perfis", render: (item) => <ProfileBadges item={item} /> },
        {
          label: "Contato",
          render: (item) => (
            <div>
              {item.document && <div className="small">{item.document}</div>}
              {item.email && <div className="text-muted small">{item.email}</div>}
              {item.phone && !item.email && <div className="text-muted small">{item.phone}</div>}
            </div>
          ),
        },
        {
          label: "Status",
          render: (item) =>
            item.isActive ? (
              <span className="badge text-bg-success" style={{ fontSize: "0.7rem" }}>Ativo</span>
            ) : (
              <span className="badge text-bg-danger" style={{ fontSize: "0.7rem" }}>Inativo</span>
            ),
        },
      ]}
      formFields={[
        {
          name: "type",
          label: "Tipo",
          type: "select",
          options: [
            { value: "PF", label: "Pessoa física" },
            { value: "PJ", label: "Pessoa jurídica" },
          ],
        },
        { name: "name",        label: "Razão social / Nome",  type: "text", required: true },
        { name: "tradeName",   label: "Nome fantasia",         type: "text" },
        { name: "document",    label: "CPF / CNPJ",           type: "text" },
        { name: "email",       label: "Email",                type: "email" },
        { name: "phone",       label: "Telefone",             type: "tel" },
        { name: "zipCode",     label: "CEP",                  type: "text" },
        { name: "street",      label: "Logradouro",           type: "text" },
        { name: "streetNumber",label: "Número",               type: "text" },
        { name: "complement",  label: "Complemento",          type: "text" },
        { name: "neighborhood",label: "Bairro",               type: "text" },
        { name: "city",        label: "Cidade",               type: "text" },
        { name: "state",       label: "UF",                   type: "text" },
        { name: "country",     label: "País",                 type: "text" },
        {
          name: "roleId",
          label: "Função",
          type: "select",
          options: funcoes.map((item) => ({ value: String(item.id), label: item.name })),
        },
        { name: "isClient",   label: "Cliente",      type: "checkbox" },
        { name: "isSupplier", label: "Fornecedor",   type: "checkbox" },
        { name: "isPartner",  label: "Sócio",        type: "checkbox" },
        { name: "isEmployee", label: "Funcionário",  type: "checkbox" },
        { name: "isActive",   label: "Ativo",        type: "checkbox" },
      ]}
      initialValues={{
        type: "PF",
        name: "",
        tradeName: "",
        document: "",
        email: "",
        phone: "",
        zipCode: "",
        street: "",
        streetNumber: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
        country: "Brasil",
        roleId: null,
        isClient: false,
        isSupplier: false,
        isPartner: false,
        isEmployee: false,
        isActive: true,
      }}
      mapToPayload={(values) => ({
        type: values.type,
        name: values.name,
        tradeName: values.tradeName || null,
        document: values.document || null,
        email: values.email || null,
        phone: values.phone || null,
        zipCode: values.zipCode || null,
        street: values.street || null,
        streetNumber: values.streetNumber || null,
        complement: values.complement || null,
        neighborhood: values.neighborhood || null,
        city: values.city || null,
        state: values.state || null,
        country: values.country || null,
        roleId: values.roleId ? Number(values.roleId) : null,
        isClient: values.isClient,
        isSupplier: values.isSupplier,
        isPartner: values.isPartner,
        isEmployee: values.isEmployee,
        isActive: values.isActive,
      })}
      permissionWrite="ENTITY_WRITE"
      renderFormExtras={({ values, setValues, setError, readOnly }) => (
        <EntityIntegrationActions
          values={values}
          setValues={setValues}
          setError={setError}
          readOnly={readOnly}
        />
      )}
    />
  );
};
